import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { userService } from '@/services/user_service'
import { providerUsersService } from '@/services/provider_users_service'
import { useAuth } from '@/hooks/useAuth'
import { useUserRoles } from '@/hooks/useUserRoles'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { useMe } from '@/hooks/useMe'
import { AllowedAction } from '@/data/allowedActions'
import { PasswordResetSection } from '@/components/PasswordResetSection'
import { toast } from 'sonner'
import { ProviderUser } from '@magnetar/admin-api-interfaces'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: optionalEmailSchema(),
  user_role_id: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface EditProviderUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  providerUser: ProviderUser
  onSuccess?: () => void
}

export function EditProviderUserDialog({
  open,
  onOpenChange,
  providerUser,
  onSuccess,
}: EditProviderUserDialogProps) {
  const { token } = useAuth()
  const { hasPermission } = useMyPermissions()
  const { user } = useMe()
  const hasEmail = !!providerUser.user?.email
  const [pendingPassword, setPendingPassword] = useState<string | undefined>(undefined)

  // 階層權限檢查：sys_admin > provider (provider_user_management)
  const canResetPassword = (() => {
    if (user?.role_type === 'sys_admin') return true
    return hasPermission('provider_user_management', AllowedAction.U)
  })()

  // Fetch provider-level roles for this provider
  const { userRoles, isLoading: isLoadingRoles } = useUserRoles({
    pageSize: 100,
    scopeType: 'provider',
    providerId: providerUser.provider_id,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: providerUser.user?.account || '',
      user_name: providerUser.user?.user_name || '',
      email: providerUser.user?.email || '',
      user_role_id: providerUser.user_role_id?.toString() || '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        account: providerUser.user?.account || '',
        user_name: providerUser.user?.user_name || '',
        email: providerUser.user?.email || '',
        user_role_id: providerUser.user_role_id?.toString() || '',
      })
      setPendingPassword(undefined)
    }
  }, [open, providerUser, form])

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    const data: {
      account: string
      user_name: string
      email?: string | null
      is_active: boolean
      password?: string
    } = {
      account: values.account,
      user_name: values.user_name,
      email: hasEmail ? providerUser.user?.email : (values.email || null),
      is_active: true,
    }

    if (pendingPassword) {
      data.password = pendingPassword
    }

    const result = await userService.updateUser(providerUser.user_id, data, token)

    if (!result.success) {
      toast.error(result.error?.message || '更新使用者失敗')
      return
    }

    // 角色綁定走 provider-user 端點（provider_users.user_role_id），
    // 這才是 /api/me/permissions 與列表讀取的綁定；寫到 users.user_role_id 不會生效。
    const newRoleId = values.user_role_id ? parseInt(values.user_role_id, 10) : null
    if (newRoleId !== (providerUser.user_role_id ?? null)) {
      const roleResult = await providerUsersService.updateProviderUserRole(
        providerUser.provider_id,
        providerUser.provider_user_id,
        newRoleId,
        token,
      )
      if (!roleResult.success) {
        toast.error(roleResult.error?.message || '更新使用者角色失敗')
        return
      }
    }

    toast.success('更新使用者成功')
    onOpenChange(false)
    onSuccess?.()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>編輯 Provider 使用者</DialogTitle>
          <DialogDescription>編輯使用者基本資料</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@domain.com"
                      disabled={hasEmail}
                      {...field}
                    />
                  </FormControl>
                  {hasEmail && (
                    <p className="text-xs text-muted-foreground">Email 已設定，無法修改</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色 (選填)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingRoles ? '載入中...' : '請選擇角色'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <PasswordResetSection
              canResetPassword={canResetPassword}
              onPasswordChange={setPendingPassword}
            />
            <DialogFooter>
              <Button type="submit">儲存</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
